import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MESSAGE } from "../utils/message.utils";
import { MESSAGE_EN } from "../utils/message-en.utils";
import { setCurrentMessages } from "../utils/translate.utils";
import { LocalStorageService } from "./local-storage.service";

const DICTIONARIES: Record<string, any> = {
  fr: MESSAGE,
  en: MESSAGE_EN,
};

// Lazy-load ES and PT to avoid circular module initialization with SharedModule
const _lazyDicts: Record<string, Promise<any>> = {
  es: import("../utils/message-es.utils").then((m) => (DICTIONARIES["es"] = m.MESSAGE_ES)),
  pt: import("../utils/message-pt.utils").then((m) => (DICTIONARIES["pt"] = m.MESSAGE_PT)),
};

const LOCALE_MAP: Record<string, string> = {
  fr: "fr-FR",
  en: "en-US",
  es: "es-ES",
  pt: "pt-BR",
};

const PAGINATOR_LABELS: Record<
  string,
  {
    itemsPerPage: string;
    nextPage: string;
    previousPage: string;
    firstPage: string;
    lastPage: string;
    of: string;
  }
> = {
  fr: {
    itemsPerPage: "Éléments par page :",
    nextPage: "Page suivante",
    previousPage: "Page précédente",
    firstPage: "Première page",
    lastPage: "Dernière page",
    of: "sur",
  },
  en: {
    itemsPerPage: "Items per page:",
    nextPage: "Next page",
    previousPage: "Previous page",
    firstPage: "First page",
    lastPage: "Last page",
    of: "of",
  },
  es: {
    itemsPerPage: "Elementos por página:",
    nextPage: "Página siguiente",
    previousPage: "Página anterior",
    firstPage: "Primera página",
    lastPage: "Última página",
    of: "de",
  },
  pt: {
    itemsPerPage: "Itens por página:",
    nextPage: "Próxima página",
    previousPage: "Página anterior",
    firstPage: "Primeira página",
    lastPage: "Última página",
    of: "de",
  },
};

@Injectable({ providedIn: "root" })
export class LanguageService {
  private localStorageService = inject(LocalStorageService);
  currentLang = signal(LanguageService.getStoredLang());

  messages = computed(
    () => DICTIONARIES[this.currentLang()] ?? DICTIONARIES["fr"],
  );

  availableLanguages = [
    { code: "fr", label: "Français" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "pt", label: "Português" },
  ];

  constructor() {
    const lang = this.currentLang();
    const lazyPromise = _lazyDicts[lang];
    if (lazyPromise) {
      // Stored lang is ES/PT: wait for the chunk then apply
      lazyPromise.then((dict) => setCurrentMessages(dict));
    } else {
      setCurrentMessages(this.messages());
    }
  }

  setLanguage(lang: string, reload = true) {
    if (!DICTIONARIES[lang] && !_lazyDicts[lang]) return;
    const prevLang = this.currentLang();
    this.localStorageService.putWithTTL("justup_lang", lang);

    if (DICTIONARIES[lang]) {
      this.currentLang.set(lang);
      setCurrentMessages(this.messages());
    }

    // LOCALE_ID is set at bootstrap, so we need to reload for Angular locale to take effect
    if (reload && prevLang !== lang) {
      window.location.reload();
    }
  }

  static getStoredLang(): string {
    const raw = localStorage.getItem("justup_lang");
    if (!raw) return "fr";
    try {
      const parsed = JSON.parse(raw);
      return parsed.value ?? raw;
    } catch {
      return raw;
    }
  }

  static getLocaleId(): string {
    return LOCALE_MAP[LanguageService.getStoredLang()] || "fr-FR";
  }
}

@Injectable({ providedIn: "root" })
export class CustomPaginatorIntl extends MatPaginatorIntl {
  constructor(private languageService: LanguageService) {
    super();
    this.applyLabels();
    effect(() => {
      this.languageService.currentLang();
      this.applyLabels();
      this.changes.next();
    });
  }

  private applyLabels() {
    const labels =
      PAGINATOR_LABELS[this.languageService.currentLang()] ??
      PAGINATOR_LABELS["fr"];
    this.itemsPerPageLabel = labels.itemsPerPage;
    this.nextPageLabel = labels.nextPage;
    this.previousPageLabel = labels.previousPage;
    this.firstPageLabel = labels.firstPage;
    this.lastPageLabel = labels.lastPage;
    this.getRangeLabel = (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) return `0 ${labels.of} ${length}`;
      const startIndex = page * pageSize;
      const endIndex = Math.min(startIndex + pageSize, Math.max(length, 0));
      return `${startIndex + 1} – ${endIndex} ${labels.of} ${length}`;
    };
  }
}
